import { useEffect, useRef, useState } from 'react'
import { authenticatedFetch, getAuthHeaders } from '../utils/auth.js'
import { LIST_ACTIONS } from '../utils/shoppingListActions.js'

const POLL_INTERVAL_MS = 3000
const FAILURE_THRESHOLD = 3
const COOLDOWN_MS = 60000

/**
 * Orchestrates chat-created "Current Shopping List" hydration for Shop.
 * Includes a circuit breaker to avoid request storms during backend degradation.
 */
export function useChatListHydrationOrchestrator({
  enabled,
  isAuthenticated,
  activeGroceryTab,
  groceryListItemCount,
  loadedSavedListId,
  addingFromSavedList,
  selectedStoreId,
  mapDisplayItems,
  chatHydrationSuppressedRef,
  chatListHydratedIdRef,
  dispatchListAction,
}) {
  const [chatHydrationNotice, setChatHydrationNotice] = useState(null)
  const failureCountRef = useRef(0)
  const cooldownUntilRef = useRef(0)

  useEffect(() => {
    if (!enabled || !isAuthenticated) return
    if (activeGroceryTab !== 'shopping') return
    if (groceryListItemCount > 0) return
    if (loadedSavedListId) return
    if (addingFromSavedList) return

    let cancelled = false
    let inFlight = false
    let sawAnyTargetList = false
    let stableTicks = 0
    let pollTimeoutId = null

    const registerHydrationFailure = () => {
      failureCountRef.current += 1
      if (failureCountRef.current >= FAILURE_THRESHOLD) {
        cooldownUntilRef.current = Date.now() + COOLDOWN_MS
        setChatHydrationNotice('Sync paused for 60s while list services recover.')
      }
    }

    const syncFromChatList = async () => {
      if (cancelled || inFlight) return
      if (chatHydrationSuppressedRef.current) return
      if (Date.now() < cooldownUntilRef.current) return
      inFlight = true
      try {
        const res = await authenticatedFetch('/saved-lists/?search=Current%20Shopping%20List&limit=20', {
          headers: getAuthHeaders(),
        })
        if (!res.ok || cancelled) {
          registerHydrationFailure()
          return
        }
        failureCountRef.current = 0
        setChatHydrationNotice(null)
        const lists = await res.json()
        if (!Array.isArray(lists) || cancelled) return
        const target = lists.find((l) => {
          const name = (l?.name || '').trim().toLowerCase()
          const meta = l?.list_metadata || {}
          return name === 'current shopping list' && meta.source === 'chat_add_items'
        })
        if (!target?.id) {
          sawAnyTargetList = false
          stableTicks = 0
          return
        }

        sawAnyTargetList = true
        const params = new URLSearchParams()
        if (selectedStoreId) params.set('store', selectedStoreId)
        const detailsRes = await authenticatedFetch(
          `/saved-lists/${target.id}${params.toString() ? `?${params.toString()}` : ''}`,
          { headers: getAuthHeaders() }
        )
        if (!detailsRes.ok || cancelled) {
          registerHydrationFailure()
          return
        }
        failureCountRef.current = 0
        setChatHydrationNotice(null)
        const list = await detailsRes.json()
        if (!list || !Array.isArray(list.items) || list.items.length === 0) return

        const mapped = mapDisplayItems(list.items)
        const nextFingerprint = JSON.stringify(
          mapped.map((it) => ({
            id: it?.id,
            item: it?.item,
            completed: !!it?.completed,
            category: it?.category,
            aisle_id: it?.aisle_id,
            aisle_name: it?.aisle_name,
          }))
        )

        if (chatListHydratedIdRef.current === `${target.id}:${nextFingerprint}`) {
          stableTicks += 1
        } else {
          chatListHydratedIdRef.current = `${target.id}:${nextFingerprint}`
          stableTicks = 0
          dispatchListAction({
            type: LIST_ACTIONS.REPLACE_FROM_LIST,
            items: mapped,
            listId: target.id,
            listName: (list.name && String(list.name).trim()) || 'Current Shopping List',
            isRecipe: false,
            preserveCurrentOnSameList: true,
          })
        }
      } catch (err) {
        registerHydrationFailure()
        console.warn('Chat shopping-list sync skipped:', err)
      } finally {
        inFlight = false
      }
    }

    const scheduleNextPoll = (delayMs) => {
      if (cancelled) return
      if (pollTimeoutId) clearTimeout(pollTimeoutId)
      pollTimeoutId = setTimeout(async () => {
        if (cancelled) return
        if (sawAnyTargetList && stableTicks >= 2) return
        await syncFromChatList()
        if (Date.now() < cooldownUntilRef.current) return
        scheduleNextPoll(POLL_INTERVAL_MS)
      }, delayMs)
    }

    scheduleNextPoll(0)
    return () => {
      cancelled = true
      if (pollTimeoutId) clearTimeout(pollTimeoutId)
    }
  }, [
    activeGroceryTab,
    addingFromSavedList,
    chatHydrationSuppressedRef,
    chatListHydratedIdRef,
    dispatchListAction,
    enabled,
    groceryListItemCount,
    isAuthenticated,
    loadedSavedListId,
    mapDisplayItems,
    selectedStoreId,
  ])

  return { chatHydrationNotice }
}
